// User Role Types
export type UserRole =
  | 'SUPER_ADMIN'
  | 'TENANT_ADMIN'
  | 'CRM_ADMIN'
  | 'SALES_DIRECTOR'
  | 'HR_MANAGER'
  | 'HR_ADMIN'
  | 'PAYROLL_MANAGER'
  | 'SALES_MANAGER'
  | 'DEPARTMENT_HEAD'
  | 'PEOPLE_MANAGER'
  | 'TEAM_LEAD'
  | 'SALES_REP'
  | 'MANAGER'
  | 'ATTENDANCE_GOVERNANCE_APPROVER'
  | 'EMPLOYEE';

// Permission Types
export type Permission = string; // e.g., 'hrms:employee:create:tenant'

// User Interface
export interface User {
  id: string;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  permissions: Permission[];
  tenantId?: string;
  tenantName?: string;
  tenantSlug?: string;
  employeeId?: string; // Linked employee record ID (for employees viewing their own profile)
  departmentId?: string;
  departmentName?: string;
  departmentCode?: string;
  positionId?: string;
  positionTitle?: string;
  positionCode?: string;
  isChiefPosition?: boolean;
  teamIds?: string[];
  managedTeamIds?: string[];
  managedDepartmentId?: string;
  avatar?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Tenant Interface
export interface Tenant {
  id: string;
  name: string;
  code: string;
  slug?: string;
  plan?: string;
  logo?: string;
  theme?: {
    primary?: string;
    secondary?: string;
  };
  timezone?: string;
  locale?: string;
  currency?: string;
  dateFormat?: string;
  timeFormat?: string;
}

// Login Request & Response
export interface LoginRequest {
  email: string;
  password: string;
  tenantSlug?: string;
}

// Backend login response user (partial)
export interface LoginResponseUser {
  id: string;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  fullName?: string;
  tenantId?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  permissions?: string[]; // Included in slim-JWT response body
  roles?: string[]; // Included in slim-JWT response body
  role?: string;
  employeeId?: string;
  departmentId?: string;
  departmentName?: string;
  departmentCode?: string;
  positionId?: string;
  positionTitle?: string;
  positionCode?: string;
  isChiefPosition?: boolean;
  teamIds?: string[];
  managedTeamIds?: string[];
  managedDepartmentId?: string;
}

// Backend login response tenant (partial)
export interface LoginResponseTenant {
  id: string;
  slug?: string;
  plan?: string;
  name?: string;
  code?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  dateFormat?: string;
  timeFormat?: string;
}

export interface LoginResponse {
  tokenType?: string;
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  user: LoginResponseUser;
  tenant?: LoginResponseTenant;
  mfaRequired?: boolean;
  backupCodes?: string[];
}

export interface MfaRequiredLoginResponse {
  success: false;
  mfaRequired: true;
  message: string;
  mfaChallengeToken: string;
  mfaChallengeExpiresIn: number;
  user: Pick<LoginResponseUser, 'id' | 'email' | 'firstName' | 'lastName'>;
  tenant?: LoginResponseTenant;
}

export interface MfaEnrollmentRequiredLoginResponse {
  success: false;
  mfaEnrollmentRequired: true;
  message: string;
  mfaEnrollmentToken: string;
  mfaEnrollmentExpiresIn: number;
  method: 'totp';
  user: Pick<LoginResponseUser, 'id' | 'email' | 'firstName' | 'lastName'>;
  tenant?: LoginResponseTenant;
}

// SuperAdmin Login Request & Response
export interface SuperAdminLoginRequest {
  email: string;
  password: string;
}

export interface SuperAdminLoginResponse {
  tokenType: 'Bearer';
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    permissions: string[];
  };
}

export type SuperAdminLoginResult =
  | SuperAdminLoginResponse
  | MfaRequiredLoginResponse
  | MfaEnrollmentRequiredLoginResponse;

// Register Request & Response
export interface RegisterRequest {
  // Company Information
  companyName: string;
  companyEmail: string;
  companyPhone?: string; // Optional company phone
  country?: string;
  timezone?: string;
  // Admin Information
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string; // Required for OTP verification
  password: string;
  // Legal Consent
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  // Optional
  referralCode?: string;
  captchaToken?: string;
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    status: string;
  };
  admin: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  nextSteps?: {
    verifyCompanyEmail: boolean;
    verifyEmail: boolean;
    verifyPhone: boolean;
    companyEmailSentTo: string;
    emailSentTo: string;
    phoneSentTo?: string;
  };
  nextAction?: 'VERIFY_ACCOUNT' | 'SIGN_IN';
  login?: {
    email: string;
    tenantSlug: string;
  };
}

// Refresh Token Request & Response
export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

export type StepUpOperation =
  | 'PASSWORD_CHANGE'
  | 'MFA_DISABLE'
  | 'MFA_SETUP'
  | 'MFA_BACKUP_CODES_REGENERATE'
  | 'PASSKEY_REGISTER'
  | 'PASSKEY_DELETE'
  | 'SESSION_REVOKE_ALL'
  | 'TRUSTED_DEVICE_TRUST'
  | 'TRUSTED_DEVICE_REVOKE_ALL'
  | 'EMAIL_IDENTITY_UPDATE'
  | 'ACCOUNT_DELETE'
  | 'SECURITY_SETTINGS'
  | 'ADMIN_IMPERSONATE'
  | 'EXPORT_DATA'
  | 'ABAC_GOVERNANCE_APPROVE'
  | 'ADMIN_USER_SESSION_REVOKE'
  | 'ADMIN_USER_MFA_FORCE_DISABLE'
  | 'ADMIN_USER_PASSKEY_RESET'
  | 'ADMIN_USER_SIGN_IN_COOLDOWN_RESET';

export interface StepUpRequest {
  operation: StepUpOperation;
  password: string;
}

export interface StepUpResponse {
  verified: boolean;
  token: string | null;
  expiresAt: string | null;
  reason?: string;
}

export interface SignInEmailIdentity {
  email: string;
  verified: boolean;
  verifiedAt: string | null;
}

export interface PendingSignInEmailIdentity {
  email: string;
  requestedAt: string;
  expiresAt: string | null;
}

export interface UserEmailIdentities {
  primary: SignInEmailIdentity;
  secondary: SignInEmailIdentity | null;
  pendingSecondary: PendingSignInEmailIdentity | null;
}

export interface SecondaryEmailRequestResult {
  message: string;
  maskedEmail: string;
  expiresAt: string;
}

// Forgot Password Request
export interface ForgotPasswordRequest {
  email: string;
  tenantSlug: string;
}

// Reset Password Request
export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

// Change Password Request
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Auth State
export interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// =============================================================================
// MFA / TOTP TYPES
// =============================================================================

export interface MFASetupResponse {
  secret: string;
  qrCodeUri?: string;
  qrCodeUrl?: string;
}

export interface MFAVerifySetupResponse {
  message: string;
  backupCodes: string[];
}

export interface MFAVerifyRequest {
  code: string;
}

export interface MFALoginVerifyRequest extends MFAVerifyRequest {
  mfaChallengeToken: string;
}

export interface MFAEnrollmentSetupRequest {
  mfaEnrollmentToken: string;
}

export interface MFAEnrollmentVerifyRequest extends MFAVerifyRequest {
  mfaEnrollmentToken: string;
}

export interface MFAStatus {
  enabled: boolean;
  method: 'totp' | 'sms' | 'email' | null;
  backupCodesRemaining: number;
  lastUsed?: string;
}

export interface MFAChallengeResponse {
  challengeId: string;
  method: 'totp' | 'sms' | 'email';
  expiresAt: string;
}

// =============================================================================
// SESSION TYPES
// =============================================================================

export interface Session {
  id: string;
  userId?: string;
  deviceInfo?: {
    browser: string;
    os: string;
    device: string;
  };
  deviceName?: string | null;
  deviceType?: string | null;
  browser?: string | null;
  os?: string | null;
  ipAddress?: string | null;
  location?: {
    city: string;
    country: string;
  };
  isCurrent?: boolean;
  current?: boolean;
  createdAt: string;
  lastActiveAt?: string;
  lastActivityAt?: string;
  expiresAt?: string;
}

export interface SessionListResponse {
  sessions: Session[];
  total?: number;
}

// =============================================================================
// PASSKEY / WEBAUTHN TYPES
// =============================================================================

export interface Passkey {
  id: string;
  name: string;
  credentialId?: string;
  lastUsed?: string;
  lastUsedAt?: string | null;
  createdAt: string;
  deviceType?: string | null;
  authenticatorName?: string | null;
  certificationStatus?: string | null;
  attestationVerified?: boolean;
}

export interface PasskeyRegisterOptionsResponse {
  challenge: string;
  options: {
    challenge: string;
    rp: {
      name: string;
      id: string;
    };
    user: {
      id: string;
      name: string;
      displayName: string;
    };
    pubKeyCredParams: Array<{
      type: 'public-key';
      alg: number;
    }>;
    timeout: number;
    attestation: 'none' | 'indirect' | 'direct';
    authenticatorSelection?: {
      authenticatorAttachment?: 'platform' | 'cross-platform';
      residentKey?: 'required' | 'preferred' | 'discouraged';
      userVerification?: 'required' | 'preferred' | 'discouraged';
      requireResidentKey?: boolean;
    };
    excludeCredentials?: Array<{
      type: 'public-key';
      id: string;
      transports?: AuthenticatorTransport[];
    }>;
  };
}

export interface PasskeyAuthenticateOptionsResponse {
  challenge: string;
  tenantId: string;
  options: {
    challenge: string;
    timeout: number;
    rpId: string;
    allowCredentials: Array<{
      id: string;
      type: 'public-key';
      transports?: ('usb' | 'nfc' | 'ble' | 'internal')[];
    }>;
    userVerification: 'required' | 'preferred' | 'discouraged';
  };
}

// =============================================================================
// OAUTH TYPES
// =============================================================================

export type OAuthProvider = 'google' | 'microsoft' | 'github' | 'okta' | 'auth0';

export interface OAuthInitRequest {
  provider: OAuthProvider;
  redirectUri?: string;
}

export interface OAuthInitResponse {
  authUrl: string;
  state: string;
}

export interface OAuthCallbackRequest {
  provider: OAuthProvider;
  code: string;
  state: string;
}

export interface OAuthProviderInfo {
  provider: OAuthProvider;
  enabled: boolean;
  name: string;
  icon?: string;
  linkedAt?: string;
  email?: string;
}

// =============================================================================
// MAGIC LINK TYPES
// =============================================================================

export interface MagicLinkSendRequest {
  email: string;
  tenantId?: string;
  tenantSlug?: string;
  redirectUri?: string;
  captchaToken?: string;
}

export interface MagicLinkSendResponse {
  success?: boolean;
  message: string;
  expiresAt?: string;
  retryAfterSeconds?: number;
}

export interface MagicLinkVerifyRequest {
  token: string;
}

// =============================================================================
// DEVICE TRUST TYPES
// =============================================================================

export interface TrustedDevice {
  id: string;
  deviceName: string | null;
  deviceType: string | null;
  browser: string | null;
  os: string | null;
  trustLevel: 'REMEMBERED' | 'VERIFIED' | 'ALWAYS_TRUSTED';
  isActive: boolean;
  ipAddress: string | null;
  country: string | null;
  city: string | null;
  isCurrent: boolean;
  trustedAt: string;
  lastUsedAt: string;
  expiresAt: string;
}

export interface DeviceTrustStatus {
  isTrusted: boolean;
  trustLevel: TrustedDevice['trustLevel'] | null;
  canSkipMfa: boolean;
  fingerprintMasked: string;
  device?: TrustedDevice;
}

export interface TrustedDeviceListResponse {
  devices: TrustedDevice[];
  total: number;
  currentDeviceIndex: number;
}

export interface TrustDeviceResponse {
  success: boolean;
  message: string;
  device: TrustedDevice;
  expiresInDays: number;
}

// =============================================================================
// PASSWORD SECURITY TYPES
// =============================================================================

export interface PasswordBreachCheckResponse {
  isBreached: boolean;
  breachCount?: number;
  recommendations?: string[];
}

export interface PasswordSecurityCheckResponse {
  strength: 'weak' | 'fair' | 'good' | 'strong';
  score: number;
  feedback: string[];
  isAcceptable: boolean;
}

// =============================================================================
// CAPTCHA TYPES
// =============================================================================

export interface CaptchaConfig {
  enabled: boolean;
  provider: 'recaptcha' | 'hcaptcha' | 'turnstile';
  siteKey: string;
  requiredFor: ('login' | 'register' | 'forgot-password')[];
}

// =============================================================================
// EMAIL / PHONE VERIFICATION TYPES
// =============================================================================

export interface EmailVerificationStatus {
  email: string;
  isVerified: boolean;
  verifiedAt?: string;
  lastSentAt?: string;
}

export interface PhoneVerificationStatus {
  phone: string;
  isVerified: boolean;
  verifiedAt?: string;
  lastSentAt?: string;
}

export interface SendPhoneVerificationRequest {
  phone: string;
}

export interface VerifyPhoneRequest {
  phone: string;
  code: string;
}

// =============================================================================
// AUDIT LOG TYPES
// =============================================================================

export interface AuthAuditLog {
  id: string;
  action: string;
  userId: string;
  ipAddress: string;
  userAgent: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface AuthAuditLogFilters {
  action?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface AuthAuditLogListResponse {
  data: AuthAuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuthAuditStats {
  totalLogins: number;
  failedLogins: number;
  uniqueDevices: number;
  suspiciousActivities: number;
}

export interface SecurityAlert {
  id: string;
  type: 'suspicious_login' | 'new_device' | 'password_changed' | 'mfa_disabled';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  metadata?: Record<string, unknown>;
  isResolved: boolean;
  createdAt: string;
}

// =============================================================================
// AUTH CONFIG TYPES
// =============================================================================

export interface AuthConfig {
  mfa: {
    enabled: boolean;
    required: boolean;
    methods: ('totp' | 'sms' | 'email')[];
  };
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    preventReuse: number;
    expiryDays?: number;
    expiryWarningDays: number;
  };
  session: {
    maxConcurrent: number;
    idleTimeout: number;
    absoluteTimeout: number;
  };
  passkeys: {
    enabled: boolean;
  };
  oauth: {
    providers: OAuthProvider[];
  };
  magicLink: {
    enabled: boolean;
    expiryMinutes: number;
  };
}

export interface PasswordExpiryStatus {
  isExpired: boolean;
  expiringSoon: boolean;
  daysUntilExpiry: number | null;
  expiredAt: string | null;
  passwordChangedAt: string | null;
  expiryDays: number | null;
  warningDaysBeforeExpiry: number;
  mustChangePassword: boolean;
}

export interface PublicTenantAuthConfig {
  passkeys: {
    enabled: boolean;
  };
  magicLink: {
    enabled: boolean;
    expiryMinutes: number;
  };
}

// =============================================================================
// PUBLIC VERIFICATION TYPES (for login flow)
// =============================================================================

/**
 * Response when login credentials are valid but user needs to verify email/phone
 */
export interface VerificationRequiredResponse {
  success: false;
  requiresVerification: true;
  message: string;
  verificationStatus: {
    emailVerified: boolean;
    companyEmailVerified: boolean;
    phoneVerified: boolean;
    maskedEmail: string;
    maskedCompanyEmail: string;
    maskedPhone?: string;
  };
  tenant: {
    slug: string;
  };
}

/**
 * Request to verify email/phone with OTP (public endpoint)
 */
export interface PublicVerifyRequest {
  email: string;
  tenantSlug: string;
  code: string;
}

/**
 * Request to resend verification OTP (public endpoint)
 */
export interface PublicResendVerificationRequest {
  email: string;
  tenantSlug: string;
}

/**
 * Response from verification endpoints
 */
export interface VerificationResult {
  success: boolean;
  message: string;
  expiresAt?: string;
  retryAfterSeconds?: number;
  emailVerified?: boolean;
  companyEmailVerified?: boolean;
  phoneVerified?: boolean;
}

/**
 * Public verification status response
 */
export interface PublicVerificationStatus {
  emailVerified: boolean;
  companyEmailVerified: boolean;
  phoneVerified: boolean;
  maskedEmail: string;
  maskedCompanyEmail: string;
  maskedPhone?: string;
}

/**
 * Type guard to check if login response requires verification
 */
export function isVerificationRequired(
  response:
    | LoginResponse
    | VerificationRequiredResponse
    | MfaRequiredLoginResponse
    | MfaEnrollmentRequiredLoginResponse,
): response is VerificationRequiredResponse {
  return 'requiresVerification' in response && response.requiresVerification === true;
}

export function isMfaRequired(
  response:
    | LoginResponse
    | VerificationRequiredResponse
    | MfaRequiredLoginResponse
    | MfaEnrollmentRequiredLoginResponse,
): response is MfaRequiredLoginResponse {
  return 'mfaRequired' in response && response.mfaRequired === true;
}

export function isMfaEnrollmentRequired(
  response:
    | LoginResponse
    | VerificationRequiredResponse
    | MfaRequiredLoginResponse
    | MfaEnrollmentRequiredLoginResponse,
): response is MfaEnrollmentRequiredLoginResponse {
  return 'mfaEnrollmentRequired' in response && response.mfaEnrollmentRequired === true;
}
